import { RegisterValues } from '@/forms/Register'
import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  type Dispatch,
  type SetStateAction,
  type ReactNode,
} from 'react'

type CurrentView = 'login' | 'verify' | 'register' | 'forgot' | 'guest' | 'convert';
type Location = 'header' | 'story';

type LoginModalContextType = {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  currentView: string
  setCurrentView: Dispatch<SetStateAction<CurrentView>>
  location: string
  setLocation: Dispatch<SetStateAction<Location>>
  /** Optional callback to be executed after a successful login */
  postLoginCallback?: any
  /** Set a callback to run after login modal closes successfully */
  setPostLoginCallback: any
  productType: any
  setProductType: any
  executePostLoginCallback: () => void
  isCallBack: boolean
  setIsCallBack: Dispatch<SetStateAction<boolean>>
  registerPrefill: RegisterValues | null;
  setRegisterPrefill: (values: RegisterValues | null) => void;
  name?: string
  setName?: Dispatch<SetStateAction<string>>
}

// LoginModalContext.tsx
const LoginModalContext = createContext<LoginModalContextType>({
  isOpen: false,
  setIsOpen: () => {},
  postLoginCallback: null,
  setPostLoginCallback: () => {},
  executePostLoginCallback: () => {},
  currentView: 'login',
  setCurrentView: () => {},
  productType: null,
  setProductType: () => {},
  isCallBack: false,
  setIsCallBack: () => {},
  registerPrefill: null,
  setRegisterPrefill: () => {},
  location: 'header',
  setLocation: () => {},
  name: '',
  setName: () => {}
});

export function LoginModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [productType, setProductType] = useState(null);
  const [currentView, setCurrentView] = useState<'login' | 'verify' | 'register' | 'forgot' | 'guest' | 'convert'>('login')
  const [location, setLocation] = useState<'header' | 'story'>('header');
  const [name, setName] = useState('');
  const [postLoginCallback, setPostLoginCallbackState] = useState<(() => void) | null>(null);
  const [isCallBack, setIsCallBack] = useState(false);
  const [registerPrefill, setRegisterPrefill] = useState<RegisterValues | null>(null);

  const isExecutingCallback = useRef(false);

  const setPostLoginCallback = useCallback((cb: () => void) => {
    if (!isExecutingCallback.current) {
      setPostLoginCallbackState(() => cb);
    }
  }, []);

  const executePostLoginCallback = useCallback(() => {
    if (postLoginCallback && !isExecutingCallback.current) {
      isExecutingCallback.current = true;
      try {
        postLoginCallback();
      } finally {
        // Reset after a short delay to allow any state updates to complete
        setTimeout(() => {
          setPostLoginCallbackState(null);
          isExecutingCallback.current = false;
        }, 100);
      }
    }
  }, [postLoginCallback]);

  return (
    <LoginModalContext.Provider
      value={{
        isOpen,
        setIsOpen,
        setPostLoginCallback,
        executePostLoginCallback,
        currentView,
        setCurrentView,
        productType,
        setProductType,
        isCallBack,
        setIsCallBack,
        registerPrefill,
        setRegisterPrefill,
        location,
        setLocation,
        name,
        setName
      }}
    >
      {children}
    </LoginModalContext.Provider>
  );
}

export const useLoginModal = (): LoginModalContextType => {
  const context = useContext(LoginModalContext)
  if (!context) {
    throw new Error('useLoginModal must be used within a LoginModalProvider')
  }
  return context
}